import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import {
    Box,
    Fab,
    Grid,
    Typography,
    Button,
    Accordion,
    AccordionSummary,
    Stepper,
    Step,
    StepLabel,
    Pagination,
    AccordionDetails,
} from '@mui/material';
import api from '../../api/api';
import dayjs from 'dayjs';
import * as XLSX from "xlsx";

import ApproveRequestDialogManager from '../commonservices/ApproveRequestDialogManager';
import ApproveRequestDialogAdmin from '../commonservices/ApproveRequestDialogAdmin';

import DownloadIcon from '@mui/icons-material/Download';
import { useAuth } from '../../contexts/AuthContext';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PersonIcon from '@mui/icons-material/Person';
import SourceIcon from '@mui/icons-material/Source';
import Person2Icon from '@mui/icons-material/Person2';
import InfoIcon from '@mui/icons-material/Info';
import DateRangeIcon from '@mui/icons-material/DateRange';
import GppGoodIcon from '@mui/icons-material/GppGood';
import GppBadIcon from '@mui/icons-material/GppBad';
import TShirtIcon from '@mui/icons-material/Checkroom';
import WeekendIcon from '@mui/icons-material/BeachAccess';
import PantsIcon from '@mui/icons-material/Accessibility';

const PAGE_SIZE = 5;

const UniformRequirementListing = () => {

    var utc = require("dayjs/plugin/utc");
    dayjs.extend(utc);

    const navigate = useNavigate();
    const { user } = useAuth();
    const [userId, setUserId] = useState(null);
    const [role, setRole] = useState(null);
    const [page, setPage] = useState(1);

    const handlePageChange = (event, value) => {
        setPage(value);
    }

    useEffect(() => {
        if (user) {
            setUserId(user['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier']);
            setRole(user['http://schemas.microsoft.com/ws/2008/06/identity/claims/role']);
        }
    }, [user]);

    const [uniformRequirementRequest, setUniformRequirementRequest] = useState([]);

    const [filteredData, paginatedData] = useMemo(() => {
        const filteredData = uniformRequirementRequest?.filter(
            req => req.employeeId == userId ||
                (role == "Manager" && req.approvalManagerId == userId) ||
                (role == "Admin" && req.managerApprovedAt)
        );
        return [filteredData, filteredData.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)];
    }, [userId, role, page, uniformRequirementRequest])


    // APIs
    const loadUniformRequirementRequest = () => {
        api.get('/api/UniformRequirement')
            .then(response => {
                setUniformRequirementRequest(response.data);
            })
            .catch(error => {
                console.log('Error fetching Uniform Requirement List', error);
            });
    }

    useEffect(() => {
        loadUniformRequirementRequest();
    }, []);

    //#region  Excel section
    const handleDownload = async (e) => {
        let wb = XLSX.utils.book_new(),
            pws = XLSX.utils.json_to_sheet([]);

        let uniformRequirementRequestHeaders = [[
            "Request Id",
            "Requester",
            "Requested At",
            "Regular Days Full Sleeve T-shirt",
            "Regular Days Half Sleeve T-shirt",
            "Saturday T-shirt",
            "Trouser",
            "T-Shirt Size",
            "Trouser Size",
            "Reason of Requirement",
            "Issued or Not Available",
            "Approval Manager",
            "Manager Approved At",
            "Manager Rejected At",
            "Manager Remarks",
            "Admin Approved At",
            "Admin Remarks",
            "Cancelled By",
            "Cancelled At",
            "Completed At"]];

        const uniformRequirementRequestData = filteredData?.map((c) => ({
            RequestId: c.requestId,
            Requester: c.employee?.name,
            RequestedAt: dayjs(c.createdAt).format("DD/MM/YYYY"),
            RegularDaysFullSleeveTshirt: c.isRegularDaysFullSleeveTshirt,
            RegularDaysHalfSleeveTshirt: c.isRegularDaysHalfSleeveTshirt,
            SaturdayTshirt: c.isSaturdayTshirt,
            Trouser: c.isTrouser,
            tShirtSize: c.tShirtSize,
            trouserSize: c.trouserSize,
            reasonOfRequirement: c.reasonOfRequirement,
            IssuedOrNotAvailable: c.isIssuedOrNotAvailable == 0 ? 'Issued' : 'Not Available',
            ApprovalManager: c.approvalManager?.name,
            ManagerApprovedAt: c.managerApprovedAt && dayjs(c.managerApprovedAt).format("DD/MM/YYYY"),
            ManagerRejectedAt: c.managerRejectedAt && dayjs(c.managerRejectedAt).format("DD/MM/YYYY"),
            ManagerRemarks: c.managerRemarks,
            AdminApprovedAt: c.adminApprovedAt && dayjs(c.adminApprovedAt).format("DD/MM/YYYY"),
            AdminRemarks: c.adminRemarks,
            CancelledBy: c.cancelledBy?.name,
            CancelledAt: c.CancelledAt && dayjs(c.CancelledAt).format("DD/MM/YYYY"),
            CompletedAt: c.completedAt && dayjs(c.completedAt).format("DD/MM/YYYY")
        }));

        XLSX.utils.sheet_add_aoa(pws, uniformRequirementRequestHeaders);
        XLSX.utils.sheet_add_json(pws, uniformRequirementRequestData, { origin: 'A2', skipHeader: true });

        XLSX.utils.book_append_sheet(wb, pws, "Uniform Requirement");

        XLSX.writeFile(wb, "Export.xlsx");
    }
    //#endregion

    //#region Manager Approval Section

    const [dialogOpen, setDialogOpen] = useState(null);
    const [selectedRequest, setSelectedRequest] = useState(null);

    const handleRejectOpenDialog = (request) => {
        setSelectedRequest(request);
        setDialogOpen('reject');
    };

    const handleCancelRequest = (request) => {

        request.cancelledAt = dayjs().utc('z').format();
        request.cancelledById = userId;

        api.put(`/api/UniformRequirement/${request.id}`, request)
            .then(response => {
                loadUniformRequirementRequest();
                handleCloseDialog();
            })
            .catch(error => {
                console.error('Error cancelling request', error);
                handleCloseDialog();
                // Handle error
            });
    };

    const handleCompleteRequest = (request) => {

        request.completedAt = dayjs().utc('z').format();

        api.put(`/api/UniformRequirement/${request.id}`, request)
            .then(response => {
                loadUniformRequirementRequest();
                handleCloseDialog();
            })
            .catch(error => {
                console.error('Error submitting request', error);
                handleCloseDialog();
                // Handle error
            });
    };

    const handleCloseDialog = () => {
        setDialogOpen(null);
        setSelectedRequest(null);
    };

    const handleApproveOpenDialog = (request) => {
        setSelectedRequest(request);
        setDialogOpen('approve');
    };

    const handleApproveManager = (remark) => {

        if (dialogOpen === 'approve') {
            selectedRequest.managerApprovedAt = dayjs().utc('z').format();
        } else {
            selectedRequest.managerRejectedAt = dayjs().utc('z').format();
        }

        selectedRequest.managerRemarks = remark;

        api.put(`/api/UniformRequirement/${selectedRequest.id}`, selectedRequest)
            .then(response => {
                loadUniformRequirementRequest();
                handleCloseDialog();
            })
            .catch(error => {
                console.error('Error approving request', error);
                handleCloseDialog();
                // Handle error
            });
    };

    const handleApproveAdmin = (remark, isIssuedOrNotAvailable) => {
        selectedRequest.adminApprovedAt = dayjs().utc('z').format();
        selectedRequest.adminRemarks = remark;
        selectedRequest.isIssuedOrNotAvailable = isIssuedOrNotAvailable == "issued" ? 0 : 1;

        api.put(`/api/UniformRequirement/${selectedRequest.id}`, selectedRequest)
            .then(response => {
                loadUniformRequirementRequest();
                handleCloseDialog();
            })
            .catch(error => {
                console.error('Error approving request', error);
                handleCloseDialog();
                // Handle error
            });
    };

    //#endregion

    return (
        <Box sx={{ p: 2 }}>
            <Box mb={2} display={'flex'} flexDirection={'row'} justifyContent={'space-between'}>
                <Typography component="p" variant="h5">Uniform Requirement</Typography>
                {
                    <Button size='small' variant="outlined" startIcon={<DownloadIcon />} onClick={handleDownload}>
                        Excel
                    </Button>
                }
            </Box>
            <Grid container spacing={2}>
                {paginatedData?.map((request) => (
                    <Grid item xs={12} key={request.id}>
                        <Accordion>
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                                aria-controls={`panel${request.id}-content`}
                                id={`panel${request.id}-header`}
                            >
                                <Box sx={{
                                    width: '100%', display: 'grid'
                                }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                        <PersonIcon sx={{ mr: 1 }} />
                                        <Typography variant="body1">
                                            <b>Requester:</b> {request.employee.name}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                        <SourceIcon sx={{ mr: 1 }} />
                                        <Typography variant="body1">
                                            <b>Request Id:</b> {request.requestId}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                        <TShirtIcon sx={{ mr: 1 }} />
                                        <Typography variant="body1">
                                            <b>Regular Days Full Sleeve T-shirt:</b> 
                                            {request.isRegularDaysFullSleeveTshirt ? " Yes" : " No"}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                        <TShirtIcon sx={{ mr: 1 }} />
                                        <Typography variant="body1">
                                            <b>Regular Days Half Sleeve T-shirt:</b> 
                                            {request.isRegularDaysHalfSleeveTshirt ? " Yes" : " No"}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                        <WeekendIcon sx={{ mr: 1 }} />
                                        <Typography variant="body1">
                                            <b>Saturday T-shirt:</b> 
                                            {request.isSaturdayTshirt ? " Yes" : " No"}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                        <PantsIcon sx={{ mr: 1 }} />
                                        <Typography variant="body1">
                                            <b>Trouser:</b> 
                                            {request.isTrouser ? " Yes" : " No"}
                                        </Typography>
                                    </Box>
                                    {request.cancelledBy &&
                                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                            <PersonIcon color='warning' sx={{ mr: 1 }} />
                                            <Typography variant="body1">
                                                <b>Cancelled By:</b> {request.cancelledBy.name}
                                            </Typography>
                                        </Box>
                                    }
                                    <Box sx={{ width: '100%', overflowX: 'auto', display: 'block' }}>
                                        <Stepper activeStep={request.adminApprovedAt || request.adminRejectedAt ? 3 : request.managerApprovedAt ? 2 : 1}>
                                            <Step key={'request'} >
                                                <StepLabel>Request</StepLabel>
                                            </Step>
                                            <Step key={'manager'}>
                                                <StepLabel {...{ error: (request.managerRejectedAt != null) }}>Manager</StepLabel>
                                            </Step>
                                            <Step key={'admin'}>
                                                <StepLabel {...{ error: (request.adminRejectedAt != null) }}>Admin</StepLabel>
                                            </Step>
                                        </Stepper>
                                    </Box>
                                </Box>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Box >
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                        <TShirtIcon sx={{ mr: 1 }} />
                                        <Typography variant="body2"><b>T-Shirt Size: </b>
                                            {request.tShirtSize}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                        <PantsIcon sx={{ mr: 1 }} />
                                        <Typography variant="body2"><b>Trouser Size: </b>
                                            {request.trouserSize}</Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                        <DateRangeIcon sx={{ mr: 1 }} />
                                        <Typography variant="body2"><b>Requested At:</b> {dayjs(request.createdAt).format("DD/MM/YYYY")}</Typography>
                                    </Box>
                                    {request.adminRemarks &&
                                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                            {request.adminApprovedAt ? <GppGoodIcon color='success' sx={{ mr: 1 }} /> : <GppBadIcon color='error' sx={{ mr: 1 }} />}
                                            <Typography variant="body2"><b>Admin Remarks:</b> {request.adminRemarks}</Typography>
                                        </Box>
                                    }
                                    {request.managerRemarks &&
                                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                            {request.managerApprovedAt ? <GppGoodIcon color='success' sx={{ mr: 1 }} /> : <GppBadIcon color='error' sx={{ mr: 1 }} />}
                                            <Typography variant="body2"><b>Manager Remark:</b> {request.managerRemarks}</Typography>
                                        </Box>
                                    }
                                    {
                                        request.completedAt == null &&
                                        <>
                                            {((role == "Admin" && !request.adminApprovedAt && !request.adminRejectedAt)
                                                || (role == "Manager" && !request.managerApprovedAt && !request.managerRejectedAt))
                                                &&
                                                <Box sx={{ width: '100%', display: 'flex', gap: 2, mb: 1, mt: 1 }}>
                                                    <Button onClick={() => handleApproveOpenDialog(request)} variant='contained' color='success' sx={{ flex: '1 1 auto' }} >
                                                        Approve
                                                    </Button>
                                                    {role != "Admin"
                                                        &&
                                                        <Button onClick={() => handleRejectOpenDialog(request)} variant='contained' color='error' sx={{ flex: '1 1 auto' }} >
                                                            Reject
                                                        </Button>
                                                    }
                                                </Box>
                                            }
                                            <Box sx={{ width: '100%', display: 'flex', gap: 2, mb: 1, mt: 1 }}>
                                                {role != "Admin" && !request.managerRejectedAt && !request.cancelledById
                                                    &&
                                                    <Button fullWidth onClick={() => handleCancelRequest(request)} variant='contained' color='warning' sx={{ flex: '1 1 auto' }} >
                                                        Cancel Request
                                                    </Button>
                                                }
                                                {role == "Requester" && !request.managerRejectedAt && !request.cancelledById
                                                    &&
                                                    <Button fullWidth onClick={() => handleCompleteRequest(request)} variant='contained' color='success' sx={{ flex: '1 1 auto' }} >
                                                        Complete
                                                    </Button>
                                                }
                                            </Box>
                                        </>
                                    }

                                </Box>
                            </AccordionDetails>
                        </Accordion>
                    </Grid>
                ))}
            </Grid>
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, mb: 6 }}>
                <Pagination
                    count={Math.ceil(filteredData.length / PAGE_SIZE)}
                    page={page}
                    siblingCount={1}
                    boundaryCount={1}
                    onChange={handlePageChange}
                    color="primary"
                />
            </Box>
            <Fab color="primary" aria-label="add" sx={{ position: 'fixed', bottom: '2rem', right: '2rem' }} onClick={() => navigate('/uniform-request/add')}>
                <AddIcon />
            </Fab>
            {role == "Manager" &&
                <ApproveRequestDialogManager open={dialogOpen != null} isApprove={dialogOpen === 'approve'} handleClose={handleCloseDialog} handleApprove={handleApproveManager} />
            }
            {role == "Admin" &&
                <ApproveRequestDialogAdmin open={dialogOpen != null} handleClose={handleCloseDialog} handleApprove={handleApproveAdmin} moduleName={"UniformRequirement"} />
            }
        </Box>
    )
}

export default UniformRequirementListing